import { cn } from '@ui/lib';
import {
  ComponentPropsWithoutRef,
  ElementType,
  ForwardedRef,
  PropsWithChildren,
  forwardRef,
} from 'react';

type TextVariant =
  | 'title-1'
  | 'title-2'
  | 'title-3'
  | 'title-4'
  | 'title-5'
  | 'caption'
  | 'display-1'
  | 'display-2'
  | 'display-3'
  | 'display-4'
  | 'display-5'
  | 'body-1'
  | 'body-2'
  | 'body-3'
  | 'body-caption'
  | 'button-sm'
  | 'button-md'
  | 'button-text-links-body-1'
  | 'button-text-links-body-2'
  | 'button-text-links-body-3';

// Polymorphic component type with proper typing
type AsProp<C extends ElementType> = {
  as?: C;
};

type PropsToOmit<C extends ElementType, P> = keyof (AsProp<C> & P);

type PolymorphicComponentProp<
  C extends ElementType,
  Props = Record<string, unknown>,
> = PropsWithChildren<Props & AsProp<C>> &
  Omit<ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>;

// Define the proper TextProps type
export type TextProps<C extends ElementType = 'div'> = PolymorphicComponentProp<
  C,
  { variant?: TextVariant }
>;

// For the variant components
type VariantProps<C extends ElementType> = Omit<TextProps<C>, 'variant'> & {
  className?: string;
};

// The base Text component
export const StyledText = forwardRef(
  <C extends ElementType = 'div'>(
    { as, children, variant = 'body-1', className, ...rest }: TextProps<C>,
    ref?: ForwardedRef<any>,
  ) => {
    const Component = as || 'div';

    return (
      <Component
        {...rest}
        ref={ref}
        className={cn(
          {
            'typography-body-1': variant === 'body-1',
            'typography-body-2': variant === 'body-2',
            'typography-body-3': variant === 'body-3',
            'typography-body-caption': variant === 'body-caption',
            'typography-title-1': variant === 'title-1',
            'typography-title-2': variant === 'title-2',
            'typography-title-3': variant === 'title-3',
            'typography-title-4': variant === 'title-4',
            'typography-title-5': variant === 'title-5',
            'typography-caption': variant === 'caption',
            'typography-display-1': variant === 'display-1',
            'typography-display-2': variant === 'display-2',
            'typography-display-3': variant === 'display-3',
            'typography-display-4': variant === 'display-4',
            'typography-button-sm': variant === 'button-sm',
            'typography-button-md': variant === 'button-md',
            'typography-button-text-links-body-1':
              variant === 'button-text-links-body-1',
            'typography-button-text-links-body-2':
              variant === 'button-text-links-body-2',
            'typography-button-text-links-body-3':
              variant === 'button-text-links-body-3',
          },
          className,
        )}
      >
        {children}
      </Component>
    );
  },
);
StyledText.displayName = 'Text';

// Variant components
export const Body1 = forwardRef(
  <C extends ElementType = 'p'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="body-1" as={as || 'p'} {...rest} />,
);
Body1.displayName = 'Body1';

export const Title1 = forwardRef(
  <C extends ElementType = 'h1'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="title-1" as={as || 'h1'} {...rest} />,
);
Title1.displayName = 'Title1';

export const Title2 = forwardRef(
  <C extends ElementType = 'h2'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="title-2" as={as || 'h2'} {...rest} />,
);
Title2.displayName = 'Title2';

export const Title4 = forwardRef(
  <C extends ElementType = 'h4'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="title-4" as={as || 'h4'} {...rest} />,
);
Title4.displayName = 'Title4';

export const Caption = forwardRef(
  <C extends ElementType = 'span'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="caption" as={as || 'span'} {...rest} />,
);
Caption.displayName = 'Caption';

export const Body2 = forwardRef(
  <C extends ElementType = 'p'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="body-2" as={as || 'p'} {...rest} />,
);
Body2.displayName = 'Body2';

export const Title5 = forwardRef(
  <C extends ElementType = 'h5'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="title-5" as={as || 'h5'} {...rest} />,
);
Title5.displayName = 'Title5';

export const Display3 = forwardRef(
  <C extends ElementType = 'h3'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="display-3" as={as || 'h3'} {...rest} />,
);
Display3.displayName = 'Display3';

// Added new components
export const Title3 = forwardRef(
  <C extends ElementType = 'h3'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="title-3" as={as || 'h3'} {...rest} />,
);
Title3.displayName = 'Title3';

export const Body3 = forwardRef(
  <C extends ElementType = 'p'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="body-3" as={as || 'p'} {...rest} />,
);
Body3.displayName = 'Body3';

export const BodyCaption = forwardRef(
  <C extends ElementType = 'p'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="body-caption" as={as || 'p'} {...rest} />,
);
BodyCaption.displayName = 'BodyCaption';

export const Display1 = forwardRef(
  <C extends ElementType = 'h1'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="display-1" as={as || 'h1'} {...rest} />,
);
Display1.displayName = 'Display1';

export const Display2 = forwardRef(
  <C extends ElementType = 'h2'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="display-2" as={as || 'h2'} {...rest} />,
);
Display2.displayName = 'Display2';

export const Display4 = forwardRef(
  <C extends ElementType = 'h4'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="display-4" as={as || 'h4'} {...rest} />,
);
Display4.displayName = 'Display4';

export const Display5 = forwardRef(
  <C extends ElementType = 'h5'>(
    { as, ...rest }: VariantProps<C>,
    ref?: ForwardedRef<any>,
  ) => <StyledText ref={ref} variant="display-5" as={as || 'h5'} {...rest} />,
);
Display5.displayName = 'Display5';